import { useEffect, useState } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { getToken, authorize } from '../../services/authService';
import {fetchCertificates, getUserInfo} from '../../services/userInfoService';
import { useAuth } from "../../utils/authContext";
import { openDatabase } from "../../utils/indexedDB";
import { useTranslation } from 'react-i18next';
import iconFirmar from '../../assets/icon-firmar.svg';
import iconValidar from '../../assets/icon-validar.svg';
import {getConfigurations} from "../../services/configService";

export const MainPage =  ({ initialConfig }) => {
    const { t } = useTranslation();
    const { setToken } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [isFetched, setIsFetched] = useState(false); // Estado para prevenir doble ejecución
    const [config, setConfig] = useState(initialConfig);
    const [loadingConfig, setLoadingConfig] = useState(!initialConfig);

    const getSentToSign = () => {
        const sentToSign = sessionStorage.getItem('sentToSign');
        return sentToSign === 'true';
    };
    // Cargar la configuración si `config` está nulo o indefinido
    const loadConfigFromSessionStorage = async () => {
        let sessionConfig = sessionStorage.getItem('configs');
        if (sessionConfig) {
            setConfig(JSON.parse(atob(sessionConfig)));
        } else {
            sessionConfig = await getConfigurations();
            sessionStorage.setItem('configs', btoa(JSON.stringify(sessionConfig)));
            setConfig(sessionConfig);
        }
        setLoadingConfig(false);
    };

    useEffect(() => {
        if (!config) {
            loadConfigFromSessionStorage();
        }
    }, [config]);

    const handleAuthorize = async () => {
        try {
            window.location.href = await authorize(
                process.env.REACT_APP_IDPID,
                process.env.REACT_APP_RESPONSE_TYPE,
                process.env.REACT_APP_STATE,
                process.env.REACT_APP_REDIRECT_URI
            );
        } catch (error) {
            console.error('Authorization failed', error);
        }
    };

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const code = query.get('code');
        const state = query.get('state');

        const cleanup = async () => {
            localStorage.clear();
            try {
                const db = await openDatabase();
                const transaction = db.transaction(['files'], 'readwrite');
                const store = transaction.objectStore('files');
                store.clear();
                sessionStorage.setItem('sentToSign', 'false');
            } catch (error) {
                console.error('Failed to clear IndexedDB:', error);
            }
        };

        if (code && state && !isFetched && !loadingConfig) {
            setIsFetched(true); // Marcar como "fetched" para evitar la segunda ejecución
            const fetchData = async () => {
                try {
                    const token = await getToken(code);
                    setToken(token);
                    const sentToSign = getSentToSign();
                    if (config?.SIGN_AUTH_REQUIRED && !sentToSign) {
                        try {
                            const userInfo = await getUserInfo(token);
                            sessionStorage.setItem('userinfo', btoa(JSON.stringify(userInfo)));
                            navigate('/signer');
                        } catch (error) {
                            console.error('Error fetching user info:', error);
                            navigate('/error');
                        }
                    } else {
                        const certificates = await fetchCertificates(token);
                        const enabledCertificates = certificates.filter(cert => cert.status.value === 'enabled' && cert.status.description === 'VIGENTE');

                        if (enabledCertificates.length === 1) {
                            localStorage.setItem('certificateId', enabledCertificates[0].id);
                            localStorage.setItem('userId', enabledCertificates[0].access[0].user_id);
                            navigate('/consent');
                        } else if (enabledCertificates.length > 1) {
                            localStorage.setItem('enabledCertificates', JSON.stringify(enabledCertificates));
                            localStorage.setItem('userId', enabledCertificates[0].access[0].user_id);
                            navigate('/select-certificate');
                        } else {
                            navigate('/signer/signDocument', {
                                state: {
                                    alertType: 'error',
                                    alertMessageKey: 'componentAlerts.nocerts'
                                }
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error obtaining token:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        } else if (!code && !state) {
            cleanup();
            setLoading(false);
        }
    }, [location.search, navigate, setToken, isFetched, loadingConfig]);

    if (loading || loadingConfig) {
        return <div>Loading...</div>;
    }

    const signAuthRequired = config ? config.SIGN_AUTH_REQUIRED : false; // Verificar si SIGN_AUTH_REQUIRED es true o false

    return (
        <main className="main-content">
            <div className="component-cards">
                <div className="page-container">
                    <div className="component-content grid-cols-2">
                        <div className="card-item">
                            <img src={iconFirmar} className="img-fluid" alt="Icono ilustracion de documento con simbolo de check" />
                            <h2 className="card-item__title"> {t('main.sign-documents')} </h2>
                            <p className="card-item__description"> {t('main.sign-details')} </p>
                            {signAuthRequired ? (
                                <button className="btn-large btn-secondary" onClick={handleAuthorize}>
                                    {t('main.sign-button')}
                                </button>
                            ) : (
                                <NavLink to="/signer" className="btn-large btn-secondary">
                                    {t('main.sign-button')}
                                </NavLink>
                            )}
                        </div>
                        <div className="card-item">
                            <img src={iconValidar} className="img-fluid" alt="Icono ilustracion de documento con simbolo de check" />
                            <h2 className="card-item__title"> {t('main.validate-signatures')} </h2>
                            <p className="card-item__description"> {t('main.validate-details')} </p>
                            <NavLink to="/validator" className="btn-large btn-secondary"> {t('main.validate-button')} </NavLink>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};
